import type { Bridge } from '@casa/bridge';

// The settings that belong to this device, not to the dashboard (docs/specs/settings.md §4.4): kept
// in the device's storage, they don't travel with the dashboard file.

export const APP_SETTINGS_PATH = 'app-settings.json';

/** Between two times of day, `HH:MM`, the screen is blanked (AP-3); `to` before `from` crosses midnight. */
export interface SleepTimes {
    enabled: boolean;
    from: string;
    to: string;
}

/** After `minutes` without a touch, the screen is dimmed (AP-5). */
export interface IdleDim {
    enabled: boolean;
    minutes: number;
}

export interface AppSettings {
    /** The screen stays on while the app is shown (AP-1); on by default, as the legacy app always was. */
    keepAwake: boolean;
    sleep: SleepTimes;
    dim: IdleDim;
    /** Someone near wakes or undims the screen (AP-2), where the device has a sensor for it. */
    wakeOnApproach: boolean;
}

/** The minutes that can be chosen for dimming (AP-5). */
export const DIM_MINUTES = [1, 2, 5, 10, 15, 30] as const;

const DEFAULTS: AppSettings = {
    keepAwake: true,
    sleep: { enabled: false, from: '23:00', to: '06:30' },
    dim: { enabled: false, minutes: 5 },
    wakeOnApproach: false,
};

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

const isRecord = (value: unknown) : value is Record<string, unknown> => typeof value === 'object' && value !== null;
const flag = (value: unknown, fallback: boolean) : boolean => (typeof value === 'boolean' ? value : fallback);
const time = (value: unknown, fallback: string) : string => (typeof value === 'string' && TIME.test(value) ? value : fallback);

/** A stored file, of any age, as settings: what is missing or wrong takes its default. */
export function parseAppSettings(parsed: unknown) : AppSettings {
    const stored = isRecord(parsed) ? parsed : {};
    const sleep = isRecord(stored.sleep) ? stored.sleep : {};
    const dim = isRecord(stored.dim) ? stored.dim : {};
    const minutes = DIM_MINUTES.find((entry) => entry === dim.minutes) ?? DEFAULTS.dim.minutes;

    return {
        keepAwake: flag(stored.keepAwake, DEFAULTS.keepAwake),
        sleep: { enabled: flag(sleep.enabled, DEFAULTS.sleep.enabled), from: time(sleep.from, DEFAULTS.sleep.from), to: time(sleep.to, DEFAULTS.sleep.to) },
        dim: { enabled: flag(dim.enabled, DEFAULTS.dim.enabled), minutes },
        wakeOnApproach: flag(stored.wakeOnApproach, DEFAULTS.wakeOnApproach),
    };
}

export async function readAppSettings(bridge: Bridge) : Promise<AppSettings> {
    const stored = await bridge.storage.read(APP_SETTINGS_PATH);
    if (!stored) {
        return DEFAULTS;
    }

    try {
        return parseAppSettings(JSON.parse(new TextDecoder().decode(stored)));
    } catch {
        return DEFAULTS;
    }
}

const changeListeners = new Set<(settings: AppSettings) => void>();

/** Told whenever the settings are written here, so that what follows them (the screen's sleep) does at once. */
export function onAppSettingsChanged(listener: (settings: AppSettings) => void) : () => void {
    changeListeners.add(listener);

    return () => changeListeners.delete(listener);
}

export async function writeAppSettings(bridge: Bridge, settings: AppSettings) : Promise<void> {
    await bridge.storage.write(APP_SETTINGS_PATH, new TextEncoder().encode(`${JSON.stringify(settings, null, 2)}\n`));
    for (const listener of [...changeListeners]) {
        listener(settings);
    }
}

/**
 * Applies the settings to the device. Returns why keeping the screen on did not work (a browser
 * without the Screen Wake Lock), or undefined when it did. Sleep times and dimming follow the
 * settings by themselves (`device/screen_sleep_view.tsx`).
 */
export async function applyAppSettings(bridge: Bridge, settings: AppSettings) : Promise<string | undefined> {
    try {
        await bridge.device.keepAwake(settings.keepAwake);

        return undefined;
    } catch (error) {
        return error instanceof Error ? error.message : String(error);
    }
}
